import { useMemo } from 'react'
import dayjs from 'dayjs'
import { usePageInfo } from 'app/frontend/base/config/queries'
import EventParticipantStatus from 'app/frontend/types/custom/EventParticipantStatus'
import Difficulty from 'app/frontend/types/custom/Difficulty'

export type BulkStatusPreviewOptionality =
  | 'any'
  | 'only_optional'
  | 'non_optional'

export type BulkStatusPreviewValues = {
  dateRange?: {
    from?: Date
    to?: Date
  }
  weekDay: string | null
  status: string
  difficulty: Difficulty | null
  optionality: BulkStatusPreviewOptionality
  characterId: string | null
  autoSignupOnly: boolean
}

const getUserParticipant = (
  event: wowaudit.EventReference,
  userId?: number,
) => {
  if (!userId) return undefined
  return event.participantReferences.find(
    (participant) => participant.characterReference.userId === userId,
  )
}

const getCharacterParticipant = (
  event: wowaudit.EventReference,
  characterId?: string | null,
) => {
  if (!characterId) return undefined
  const parsedCharacterId = Number(characterId)
  return event.participantReferences.find(
    (participant) =>
      participant.characterId === parsedCharacterId ||
      participant.altCharacterId === parsedCharacterId,
  )
}

const matchesOptionality = (
  event: wowaudit.EventReference,
  optionality: BulkStatusPreviewOptionality,
) => {
  if (optionality === 'any') return true
  if (optionality === 'only_optional') return event.optional
  return !event.optional
}

export const getBulkStatusPreviewEvents = (
  events: wowaudit.EventReference[],
  values: BulkStatusPreviewValues,
  userId?: number,
) => {
  if (!values.dateRange?.from || !values.dateRange.to || !values.status) {
    return []
  }

  return events.filter((event) => {
    if (event.status === 'locked') {
      return false
    }

    const eventDate = dayjs(event.date)
    const fromDate = dayjs(values.dateRange?.from)
    const toDate = dayjs(values.dateRange?.to)
    if (
      eventDate.isBefore(fromDate, 'day') ||
      eventDate.isAfter(toDate, 'day')
    ) {
      return false
    }
    if (values.weekDay && eventDate.day() !== Number(values.weekDay)) {
      return false
    }
    if (values.difficulty && event.primaryDifficulty !== values.difficulty) {
      return false
    }
    if (!matchesOptionality(event, values.optionality)) {
      return false
    }

    const characterParticipant = getCharacterParticipant(
      event,
      values.characterId,
    )
    if (values.characterId && !characterParticipant) {
      return false
    }

    const userParticipant = getUserParticipant(event, userId)
    if (values.autoSignupOnly && !userParticipant) {
      return false
    }

    const participant = characterParticipant ?? userParticipant
    if (!participant) {
      return false
    }

    return participant.status !== (values.status as EventParticipantStatus)
  })
}

const useBulkStatusPreviewEvents = (
  events: wowaudit.EventReference[],
  values: BulkStatusPreviewValues,
) => {
  const {
    data: { user },
  } = usePageInfo()

  return useMemo(
    () => getBulkStatusPreviewEvents(events, values, user?.id),
    [events, values, user?.id],
  )
}

export default useBulkStatusPreviewEvents
